import { isEmpty } from 'lodash';
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useFormContext } from '@/components/react-hook-form';
import {
  AISuggestionMatch,
  AISuggestionsMatcherArrayField,
  AISuggestionsMatcherVariant,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.types';
import { isActionableSuggestionMatch } from '@/modules/aiSuggestions/AISuggestionsMatcher/AISuggestionsMatcher.utils';
import {
  AiSuggestionsMatcherContext,
  getDefaultMatcherConfigs,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';
import {
  ArrayFieldToUpdate,
  useUpdateSubformArrayField,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/hooks/useUpdateSubformArrayField';
import {
  ARRAY_FIELD_DEFAULT_VALUE_FN,
  getConfigsForAISuggestionsMatcherVariant,
} from '@/modules/aiSuggestions/AISuggestionsMatcher/matcherVariant.configs';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useDPFormRecipientTrackerContext } from '@/modules/dispositiveProvisions/contexts/DPFormRecipientTracker.context';
import { Fields as BasicInformationSubformFields } from '@/modules/entities/BasicInformationSubform/BasicInformationSubform.types';
import { useEntityDetailsContext } from '@/modules/entities/contexts/entityDetails/entityDetails.context';
import { AiSuggestionAcceptanceStatus } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

function useContextValue(): AiSuggestionsMatcherContext {
  const matcherRef = useRef<HTMLDivElement>(null);

  const { entityType } = useEntityDetailsContext();
  const { acknowledgeSuggestion } = useSuggestionAcknowledgementContext();
  const { handleNewlyAddedRecipient } = useDPFormRecipientTrackerContext();
  // Only the single-grantor branch uses these form values. Array updates use
  // their own form types in useUpdateSubformArrayField.
  const { getValues, reset, setValue, setShouldBlockNavigation } =
    useFormContext<BasicInformationSubformFields>();

  const { updateArrayFields, appendArrayFields } = useUpdateSubformArrayField();

  const [matcherVariant, setMatcherVariant] =
    useState<AiSuggestionsMatcherContext['matcherVariant']>(null);

  const [matchedSuggestions, setMatchedSuggestions] = useState<
    AiSuggestionsMatcherContext['matchedSuggestions']
  >({});

  const [newlyAddedIDs, setNewlyAddedIDs] = useState<
    AiSuggestionsMatcherContext['newlyAddedIDs']
  >([]);

  const [arrayFieldsWithDuplicates, setArrayFieldsWithDuplicates] = useState<
    AiSuggestionsMatcherContext['arrayFieldsWithDuplicates']
  >({});

  const [duplicateSelections, setDuplicateSelections] = useState<
    AiSuggestionsMatcherContext['duplicateSelections']
  >({});

  useEffect(() => {
    // Reset the matched suggestions when the matcher variant changes.
    setMatchedSuggestions({});
    setNewlyAddedIDs([]);
    setArrayFieldsWithDuplicates({});
    setDuplicateSelections({});
    diagnostics.debug(
      '[AISuggestionsMatcherProvider][useEffect] matcherVariant changed',
      {
        matcherVariant,
      }
    );
  }, [matcherVariant]);

  const matcherConfigs = useMemo(
    () =>
      matcherVariant
        ? getConfigsForAISuggestionsMatcherVariant(entityType, matcherVariant)
        : getDefaultMatcherConfigs(),
    [entityType, matcherVariant]
  );

  /**
   * Handles the committing a subset of matched (or rejected) suggestions.
   * The matched suggestions are committed to the form and acknowledged as
   * accepted, and the rejected suggestions are acknowledged as rejected, and
   * removed from the matchedSuggestions state.
   *
   * @param matchedSuggestionsToCommit - The matched suggestions to commit.
   */
  const handleCommitMatchedSuggestions = useCallback(
    (matchedSuggestionsToCommit: Record<string, AISuggestionMatch>) => {
      if (
        isEmpty(matchedSuggestionsToCommit) ||
        !matcherVariant ||
        !matcherConfigs
      ) {
        return;
      }

      const {
        subformSingleFieldUpdateProps,
        subformArrayFieldUpdateProps,
        subformArrayFieldOnConflict,
      } = matcherConfigs;

      // Separate matched suggestions (have actual matches) from ignored suggestions
      const actuallyMatchedSuggestions: AISuggestionMatch[] = [];
      const ignoredSuggestions: AISuggestionMatch[] = [];

      Object.values(matchedSuggestionsToCommit).forEach((match) => {
        if (isActionableSuggestionMatch(match)) {
          actuallyMatchedSuggestions.push(match);
        } else {
          ignoredSuggestions.push(match);
        }
      });

      // Only need to block nav if there are ONLY ignored suggestions.
      // Otherwise, no form updates are triggered and the ignored suggestions are not submitted when Saving and Closing the form.
      // If there are actuallyMatchedSuggestions, the form will become dirty and trigger a real navigation block if the user tries to navigate away without saving.
      if (
        ignoredSuggestions.length > 0 &&
        actuallyMatchedSuggestions.length === 0
      ) {
        setShouldBlockNavigation(true);
      }

      // Only update the form with actually matched suggestions (not ignored ones)
      if (actuallyMatchedSuggestions.length > 0) {
        if (matcherVariant === AISuggestionsMatcherVariant.SINGLE_GRANTOR) {
          if (!subformSingleFieldUpdateProps) {
            throw new Error(
              'subformSingleFieldUpdateProps is required for SINGLE_GRANTOR matcher variant'
            );
          }
          const { singleFieldFullPath, mapperFunc } =
            subformSingleFieldUpdateProps;
          const newInput = mapperFunc(actuallyMatchedSuggestions);
          if (newInput) {
            setValue(singleFieldFullPath, newInput);
            reset(getValues());
            setShouldBlockNavigation(true);
          }
        } else {
          // Update the array fields of the current subform with the matched
          // grantors/trustees/successorTrustees/trustAdvisors/beneficiaries.
          const updateProps =
            subformArrayFieldUpdateProps.map<ArrayFieldToUpdate>((f) => {
              const defaultValueFn =
                ARRAY_FIELD_DEFAULT_VALUE_FN[f.arrayFieldFullPath];
              return {
                arrayFieldFullPath: f.arrayFieldFullPath,
                newInputs: f.mapperFunc(actuallyMatchedSuggestions),
                idField: f.idField,
                defaultValueFn: defaultValueFn
                  ? defaultValueFn
                  : () => undefined,
                omitPlaceholder: f.omitPlaceholder,
                insertionOrder: f.insertionOrder,
                allowDuplicateIds: f.allowDuplicateIds,
                onConflict: f.onConflict,
                sortByNumericField: f.sortByNumericField,
              };
            });

          // A field's own `onConflict` overrides the variant-level default, so a
          // single variant can mix strategies (e.g. BUSINESS_PEOPLE appends key
          // people but overwrites beneficial owners).
          const overwriteUpdates = updateProps.filter(
            (p) => (p.onConflict ?? subformArrayFieldOnConflict) === 'overwrite'
          );
          const appendUpdates = updateProps.filter(
            (p) => (p.onConflict ?? subformArrayFieldOnConflict) === 'append'
          );

          if (overwriteUpdates.length > 0) {
            updateArrayFields(overwriteUpdates);
          }
          if (appendUpdates.length > 0) {
            // When appending array fields, we need to show the duplicates
            // resolver card(s).
            const arrayFieldsWithDuplicates = appendArrayFields(appendUpdates);
            setArrayFieldsWithDuplicates(arrayFieldsWithDuplicates);
          }
        }
      }

      // Set the acceptance status for actually matched suggestions
      actuallyMatchedSuggestions.forEach((match) => {
        acknowledgeSuggestion({
          suggestionID: match.suggestion.id,
          status: AiSuggestionAcceptanceStatus.Accepted,
          clientProfileId: match.clientProfileId,
          clientOrganizationId: match.clientOrganizationId,
          entityId: match.entityId,
          testamentaryEntityId: match.testamentaryId,
        });

        // Only DP recipient matches that resolve to a testamentary entity are
        // tracked here — the tracker is what triggers the "navigate to manage
        // DP" prompt for newly added TE recipients.
        if (match.testamentaryId) {
          handleNewlyAddedRecipient({
            id: match.testamentaryId,
            displayName:
              match.suggestion.dpRecipient?.recipientEntityName || '',
          });
        }
      });

      // Set the acceptance status for ignored suggestions
      ignoredSuggestions.forEach((match) => {
        acknowledgeSuggestion({
          suggestionID: match.suggestion.id,
          status: AiSuggestionAcceptanceStatus.Rejected,
        });
      });

      // Clear the matched suggestions that we committed, from the matchedSuggestions state.
      setMatchedSuggestions((prev) => {
        const updatedSuggestions = { ...prev };
        // Remove the keys that we've already processed
        Object.keys(matchedSuggestionsToCommit).forEach((key) => {
          delete updatedSuggestions[key];
        });
        return updatedSuggestions;
      });
    },
    [
      matcherVariant,
      matcherConfigs,
      getValues,
      reset,
      setValue,
      setShouldBlockNavigation,
      updateArrayFields,
      appendArrayFields,
      acknowledgeSuggestion,
      handleNewlyAddedRecipient,
    ]
  );

  const confirmRow = useCallback(
    (suggestionId: string) => {
      // Confirming the row means that the user has matched the suggestion
      // to an existing or newly created entity/profile/org, so the suggestion
      // match should be in the matchedSuggestions state.
      const rowMatch = matchedSuggestions[suggestionId];
      if (!rowMatch) return;
      handleCommitMatchedSuggestions({
        [suggestionId]: rowMatch,
      });
    },
    [handleCommitMatchedSuggestions, matchedSuggestions]
  );

  const ignoreRow = useCallback(
    (suggestionId: string) => {
      // Ignoring the row means that the user has rejected the suggestion,
      // so the suggestion match will not be in the matchedSuggestions state.
      // We just need to pass the minimal data to the handleCommit function,
      // which is just the suggestion id.
      handleCommitMatchedSuggestions({
        [suggestionId]: {
          suggestion: { id: suggestionId },
        } as AISuggestionMatch,
      });
    },
    [handleCommitMatchedSuggestions]
  );

  const resolveDuplicate = useCallback(
    (arrayFieldFullPath: AISuggestionsMatcherArrayField) => {
      diagnostics.debug('[AISuggestionsMatcherProvider][resolveDuplicate]', {
        arrayFieldFullPath,
      });
      setArrayFieldsWithDuplicates((prev) => {
        const updatedArrayFieldsWithDuplicates = { ...prev };
        const count = updatedArrayFieldsWithDuplicates[arrayFieldFullPath] || 0;
        if (count >= 1) {
          updatedArrayFieldsWithDuplicates[arrayFieldFullPath] = count - 1;
        }
        return updatedArrayFieldsWithDuplicates;
      });
    },
    []
  );

  return useMemo(
    () => ({
      matcherVariant,
      setMatcherVariant,
      matchedSuggestions,
      setMatchedSuggestions,
      handleCommitMatchedSuggestions,
      confirmRow,
      ignoreRow,
      matcherRef,
      matcherConfigs,
      newlyAddedIDs,
      setNewlyAddedIDs,
      arrayFieldsWithDuplicates,
      setArrayFieldsWithDuplicates,
      resolveDuplicate,
      duplicateSelections,
      setDuplicateSelections,
    }),
    [
      matcherVariant,
      matchedSuggestions,
      handleCommitMatchedSuggestions,
      confirmRow,
      ignoreRow,
      matcherConfigs,
      newlyAddedIDs,
      arrayFieldsWithDuplicates,
      resolveDuplicate,
      duplicateSelections,
    ]
  );
}

export function AISuggestionsMatcherProvider({ children }: PropsWithChildren) {
  const contextValue = useContextValue();

  return (
    <AiSuggestionsMatcherContext.Provider value={contextValue}>
      {children}
    </AiSuggestionsMatcherContext.Provider>
  );
}
